import { useCallback, useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import {
  useFeatureFlagEnabled,
  useFeatureFlagPayload,
  usePostHog
} from '@posthog/react'

export type FeatureFlags =
  | 'manual-override-logging' // A test flag
  | 'Portal-CanSeeDesignMatchesPage'

/**
 * This file largely created by Claude. With some post-edits.
 *
 * Feature flag architecture.
 *
 * Every flag is created at 100% coverage and acts as a global on/off gate.
 *
 * User-level targeting is handled server-side by PostHog: we identify() the
 * signed-in user (see PostHogIdentify), so a flag's release conditions can
 * filter by distinct_id directly. `useFeatureFlagEnabled` already reflects
 * that decision for the current user.
 *
 * Project targeting is driven by the flag's JSON payload, matched against the
 * current Project - normally the route's `:projectId` param, but callers can
 * pass an explicit override when the Project isn't in the URL - because
 * PostHog has no notion of which Project the user is currently viewing.
 *
 *   - No payload            -> globally ON (the boolean gate is the answer)
 *   - Payload allow-list(s) -> ON only when the current Project appears in the
 *                              relevant list
 */
export type FeatureFlagPayload = {
  projects: string[]
}

export type FeatureGateResult = {
  /** Final decision after applying the payload allow-list (if any). */
  isEnabled: boolean
  /** PostHog has not resolved the flags response yet. */
  isLoading: boolean
}

/**
 * True once PostHog has resolved the flags response.
 *
 * This cannot be inferred from an individual flag's value. A flag that is
 * Disabled in PostHog is omitted from the flags response entirely, so
 * `useFeatureFlagEnabled` returns `undefined` for *both* "not loaded yet" and
 * "loaded, but this flag is off" — treating `undefined` as loading leaves the
 * gate stuck in isLoading forever once a flag is disabled.
 *
 * onFeatureFlags() is the reliable signal: it fires immediately if flags have
 * already loaded (so there is no race for components that mount late), and
 * again on every reload. We read hasLoadedFlags live rather than latching a
 * boolean, because posthog.reset() on sign-out (see PostHogIdentify) clears it
 * and refetches — a latch would report "loaded" during that window.
 */
function useHasLoadedFlags(): boolean {
  const posthog = usePostHog()

  return useSyncExternalStore(
    useCallback(onChange => posthog.onFeatureFlags(onChange), [posthog]),
    () => !!posthog?.featureFlags?.hasLoadedFlags,
    () => false
  )
}

/**
 * True once PostHog's distinct_id matches the signed-in user, or there is no
 * signed-in user to wait for.
 *
 * On a hard page refresh, PostHog fetches flags for the anonymous pre-identify()
 * distinct_id before PostHogIdentify has had a chance to identify() the signed-in
 * user (it waits for the auth check to resolve first). That anonymous response can
 * report `hasLoadedFlags: true` with the flag OFF, which would otherwise cause a
 * premature/incorrect redirect before the post-identify() reload arrives.
 */
function useIsIdentified(): boolean {
  const posthog = usePostHog()
  const { checked, userUuid } = useSelector(
    (state: { auth: { checked: boolean; userUuid: string | null } }) =>
      state.auth
  )

  if (!checked || !userUuid) {
    return true
  }

  return posthog?.get_distinct_id?.() === userUuid
}

/**
 * Resolve a single feature flag for the current context.
 *
 * Wraps `useFeatureFlagEnabled` (the global gate, already user-aware via
 * identify()) and `useFeatureFlagPayload` (the optional Project
 * allow-list), then combines them with the current route.
 *
 * @param projectIdOverride Pass this when the current Project is known but
 * isn't in the URL route (e.g. Account Settings, where a project picker
 * chooses the Project outside of routing). Falls back to the route's
 * `:projectId` param when omitted. Since this is a plain hook argument, the
 * flag naturally re-evaluates on every render, including when the caller's
 * selected Project changes.
 */
export function useFeatureGate(
  featureFlag: FeatureFlags,
  projectIdOverride?: string
): FeatureGateResult {
  // Default to `false` so a flag missing from the response (Disabled, deleted,
  // or outside its rollout) reads as off rather than as undefined.
  const enabled = useFeatureFlagEnabled(featureFlag, false)
  const payload = useFeatureFlagPayload(featureFlag) as
    | FeatureFlagPayload
    | undefined
  const hasLoadedFlags = useHasLoadedFlags()
  const isIdentified = useIsIdentified()

  const { projectId: routeProjectId } = useParams()
  const projectId = projectIdOverride ?? routeProjectId

  if (!hasLoadedFlags || !isIdentified) {
    return { isEnabled: false, isLoading: true }
  }

  // OFF Globally or Flag is filtered for a user which isn't the current user.
  if (!enabled) {
    return { isEnabled: false, isLoading: false }
  }

  // No payload, so no further checks required: flag is either Globally ON or ON for the current user.
  if (!payload) {
    return { isEnabled: true, isLoading: false }
  }

  if (!isPayloadValid(payload)) {
    console.warn(
      `Feature flag "${featureFlag}" has an invalid payload:`,
      payload
    )
    return { isEnabled: false, isLoading: false }
  }

  // Payload present, so check we have a Project context and that the current Project is in the allow-list.
  const hasContext = !!projectId

  if (!hasContext) {
    console.warn(
      `Feature flag "${featureFlag}" has a payload but no Project context is available.`
    )
    return { isEnabled: false, isLoading: false }
  }

  const isAllowed = hasContext && payload.projects.includes(projectId)

  return { isEnabled: isAllowed, isLoading: false }
}

function isPayloadValid(payload: FeatureFlagPayload) {
  return (
    payload.projects &&
    Array.isArray(payload.projects) &&
    payload.projects.length > 0
  )
}
